import {IReg, IRegPointer, IRegsPointer, REG} from "lib/commons/registry";
import {MsgLabel, MsgOver, OSkinableInit} from "back/commons/basis";
import {DOM, JSX} from "lib/commons/xml/dom";
import {AreaAsync, AREAS, ControlArea, IControlAreaContext} from "lib/commons/areas";
import {Button, OButtonInit} from "back/commons/widgets/buttons";
import {POPUP} from "back/commons/widgets/popups";
import {FORMS} from "lib/commons/forms";
import {ERROR} from "lib/core/errorReport";
import {IChainEnv} from "lib/wsp/chain";
import {IWspEnv, IWspUiEnv, JInfoWsp, JWspTypeInst, WSP, Wsp} from "lib/wsp/wsp";
import "back/wsp/dialogs/wspPropsAreas";
import {WspTypeSelector} from "back/wsp/widgets/wspType/wspTypeSelector";
import {DOMSH} from "lib/commons/xml/domsh";
import {Tabs} from "back/commons/widgets/tabs";
import {BaseAreaViewAsync, IView, IViewsContainer, OViewVisitOptions} from "lib/commons/views";
import {WSP_DRF} from "lib/wsp/drf";
import {WSP_DRV} from "lib/wsp/drv";
import {IO} from "lib/commons/io/io";
import {OWspsMonitoringInit, WspsMonitoring} from "back/wsp/widgets/wsp/wspsMonitoring";
import {IPopupable} from "back/commons/widgets/popupable";
import {ACTION} from "lib/commons/actions";
import {ExportWspAction} from "back/wsp/actions/wspActions";
import {AppFrameDeskFeat, IApp} from "back/core/appFrame";


/**
 * Écran des propriétés d'un atelier
 */

export interface OWspPropsInit<E> extends OSkinableInit {
	reg: IReg<E>
	checkDirtyOnClose?: boolean
	disableRevert?: boolean
	exportScwsp?: boolean
}

abstract class WspProps<E extends IWspUiEnv | IChainEnv> extends BaseAreaViewAsync<E> {
	public reg: IReg<E>;
	protected config: OWspPropsInit<E>;

	/**
	 * Lists extpoints des areas de la fenêtre
	 */
	abstract get areaList(): string[];

	protected _form: HTMLFormElement;
	protected _msg: MsgLabel;
	protected _emptyData: Dict<any> = {};
	protected _lastWspData: Jsonisable;
	protected _fixedWspData: Jsonisable = {};

	protected _revertBtn: Button;
	protected _saveBtn: Button;
	protected _closeBtn: Button;
	protected _exportBtn: Button;

	get wsp(): Wsp | null {return (this.reg.env as IWspUiEnv).wsp || null};

	protected async _initialize(init: OWspPropsInit<E>): Promise<void> {
		this.config = Object.create(init);// L'objet init est mutable et mémorisé => clone sans modifier l'objet initial
		this.reg = REG.createSubReg(init.reg);
		if (this.wsp) {
			(this.reg.env as IWspUiEnv).place = this.wsp.newPlaceWsp();
			(this.reg.env as IWspUiEnv).place.eventsMgr.on("wspLiveStateChange", this.onWspLiveStateChange.bind(this));
			(this.reg.env as IWspUiEnv).place.eventsMgr.on("wspLiveFullLoadStateChange", this.onWspLiveStateChange.bind(this));
		}

		if (!this.config.disableRevert) this._revertBtn = <Button î={{disabled: true, label: "Rétablir", uiContext: "dialog"} as OButtonInit} onclick={this.onRevert}/> as Button;
		this._saveBtn = <Button î={{disabled: true, label: "Enregistrer", uiContext: "dialog"} as OButtonInit} class="default" onclick={this.onSave}/> as Button;
		this._closeBtn = <Button î={{disabled: false, label: "Annuler", uiContext: "dialog"} as OButtonInit} onclick={this.onClose}/> as Button;
		if(init.exportScwsp && this.reg.hasPerm("read.node.export")) this._exportBtn = <Button î={{label: "Exporter une archive de l'atelier", uiContext: "dialog"} as OButtonInit} onclick={this.onExport}/> as Button;

		this._form = <form id="form"/> as HTMLFormElement;
		this._msg = <MsgLabel/> as MsgLabel;

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("standard-dialog", sr);
		this.reg.installSkin("form-control-areas", sr);
		this.reg.installSkin("c-wsp-props", sr);
		this._initAndInstallSkin(this.localName, init);
		DOM.append(sr, this._msg,
			this._form,
			<div id="footer">
				{this._exportBtn ? [
					this._exportBtn,
					<div style="flex:1000"/>
				] : undefined}
				{this._revertBtn}
				{this._saveBtn}
				{this._closeBtn}
			</div>
		);

		let checkDirtyRequest = 0;
		const checkBtnsState = () => {
			if (checkDirtyRequest) {
				clearTimeout(checkDirtyRequest);
				checkDirtyRequest = 0;
			}
			checkDirtyRequest = setTimeout(this.refreshBtnsState.bind(this), 300) as any;
		};
		this._form.addEventListener('input', checkBtnsState);
		this._form.addEventListener('change', checkBtnsState);

		this.addEventListener('keydown', (ev) => {
			if (ev.key === 'Enter' && ACTION.isAccelPressed(ev)) {
				ev.preventDefault();
				ev.stopImmediatePropagation();
				this._saveBtn.click();
				return true;
			}
			return false;
		});

	}

	protected async initForm(form: HTMLFormElement): Promise<void> {
		this.initFormELt(form);
		await AREAS.applyLayout(this._form, this.reg.mergeLists<ControlArea>(...this.areaList), this.areasContext(), true);
		this._emptyData = FORMS.formToJson(this._form);
	}

	protected initFormELt(form: HTMLFormElement): void {
		form.appendChild(
			<fieldset class="fieldslist">
				<div class="fields" area-ids="*"/>
			</fieldset>);
	}

	onViewShown(): void {
		if (this.wsp) {
			this._msg.setStandardMsg("loading");
			// ATTENTION : l'init du form est dépendante du reg enrichi par wspMetaUi. Le load de wspMetaUi est donc nécessaire
			this.wsp.waitForWspMetaUiAvailable().then(async (wsp) => {
				await this.initForm(this._form);
				await this.updateForm(false);
				this._msg.setStandardMsg(null);
			}).catch(async () => {
				await this.initForm(this._form);
				await this.updateForm(false);
				this._msg.setStandardMsg(null);//statut déjà affiché par ailleurs
				//this._msg.setCustomMsg("Chargement de l'atelier partiel", "error");
			});
		} else
			this.initForm(this._form).then(async () => {
				await this.updateForm(false);
			});
	}

	/**
	 * Contexte des areas
	 */
	protected areasContext(): IControlAreaContext & IRegPointer<E> {
		return {
			reg: this.reg,
			buildControlLabel: true
		};
	}

	/**
	 * Construit la liste des propriétés à exploiter (fournies par le FORM et les fixed)
	 */
	protected async getParamsToSave(): Promise<Jsonisable> {
		return Object.assign(FORMS.formToJson(this._form), this._fixedWspData);
	}

	/**
	 * Construit la liste des propriétés courantes du wsp (ie initiales : non modifiées par le form)
	 */
	protected async getIniParams(): Promise<Jsonisable> {
		return this.wsp ? this.wsp.makeParamsForUpdate() : this._emptyData;
	}

	/**
	 * Action de validation
	 * @param ev
	 */
	protected _onSaveShowWaitMsg: boolean = true;

	protected async onSave(this: Button, ev: Event): Promise<void> {
		if (!this.disabled) {
			const me = DOMSH.findHost<WspProps<E>>(this);
			if (me._form.reportValidity()) {
				me._listenLiveStateChange = false;
				try {
					this.disabled = true;
					if (me._onSaveShowWaitMsg) {
						await new MsgOver().setCustomMsg("Veuillez patienter...", "info").showMsgOver(me).waitFor(
							me._doExecuteValid()
						);
					} else
						await me._doExecuteValid();
				} finally {
					me._listenLiveStateChange = true;
					// Désactivation du listner d'update auto de l'interface le temps du save async (via _listenLiveStateChange)
					// => update explicite utile
					me.onWspLiveStateChange(me.wsp);
				}
			} else
				POPUP.showNotifWarning("Veuillez corriger le formulaire.", this);
		}
	}

	/**
	 * Action de validation (sans contrôle déjà réalisé)
	 * @param ev
	 */
	abstract _doExecuteValid(): Promise<void>;

	/**
	 * Action de fermeture / abandon
	 * @param ev
	 */
	protected async onClose(this: Button, ev: Event): Promise<void> {
		const me = DOMSH.findHost<WspProps<E>>(this);
		if (!me.config.checkDirtyOnClose || !await me.isDirty() || await POPUP.confirm("Attention, vos modifications n'ont pas été enregistrées.", me, {cancelLbl: "Annuler", okLbl: "Quitter sans enregistrer"})) {
			return me._doClose();
		}
	}

	/**
	 * Action de la fermeture
	 * @param ev
	 */
	async _doClose(returnValue?: any): Promise<void> {
		POPUP.findPopupableParent(this).close(returnValue);
	}

	/**
	 * Action de revert des modifications
	 * @param ev
	 */
	protected async onRevert(this: Button, ev: Event): Promise<void> {
		if (!this.disabled) {
			const me = DOMSH.findHost<WspProps<E>>(this);
			return me._doRevert();
		}
	}

	/**
	 * Action de revert
	 * @param ev
	 */
	async _doRevert(): Promise<void> {
		return this.updateForm(true);
	}

	protected onExport(this: Button, ev: Event) {
		const me = DOMSH.findHost<WspProps<E>>(this);
		new ExportWspAction().execute(me);
	}

	/**
	 * Retourne true si au moins une valeur du formulaire a changé
	 */
	protected async isDirty(): Promise<boolean> {
		const wspData = FORMS.formToJson(this._form);
		/*console.log("DIRTY :: ", JSON.stringify(wspData) !== JSON.stringify(this._lastWspData));
		console.log("LAST :: ", JSON.stringify(this._lastWspData));
		console.log("NEW  :: ", JSON.stringify(wspData));*/
		return JSON.stringify(wspData) !== JSON.stringify(this._lastWspData);
	}

	/**
	 * Rafraichissement des boutons
	 */
	protected async refreshBtnsState(isPendingState?:true|undefined): Promise<void> {
		const dirty = await this.isDirty();
		this._closeBtn.label = "Fermer";
		if (this._revertBtn) this._revertBtn.disabled = isPendingState || !dirty;
		if (this._exportBtn) this._exportBtn.disabled = isPendingState;
		this._form.checkValidity()
		this._saveBtn.disabled = isPendingState || !dirty;
	}

	private _pendingUpdtFormCycle: number;
	protected async updateForm(clear: boolean = true): Promise<void> {
		const currentUpdtFormCycle = this._pendingUpdtFormCycle = Date.now();
		this._msg.setStandardMsg("loading");
		DOM.setHidden(this._form, true);
		try {
			await this.refreshBtnsState(true);
			if (currentUpdtFormCycle != this._pendingUpdtFormCycle) return;//raceCond
			await this.wsp?.waitForLoad();
			if (currentUpdtFormCycle != this._pendingUpdtFormCycle) return;//raceCond
			if (this.wsp?.migrating) {
				this._msg.setCustomMsg("Migration de l'atelier en cours...");
				await this.wsp.migrating;
				if (currentUpdtFormCycle != this._pendingUpdtFormCycle) return;//raceCond
			} else if (this.wsp?.fullLoading) {
				this._msg.setCustomMsg("Chargement de l'atelier en cours...");
				await this.wsp.fullLoading;
				if (currentUpdtFormCycle != this._pendingUpdtFormCycle) return;//raceCond
			}
			if (clear) FORMS.jsonToForm(this._emptyData, this._form);
			FORMS.jsonToForm(await this.getIniParams(), this._form, true, true);
			await FORMS.checkAsyncValidity(this._form);
			if (currentUpdtFormCycle != this._pendingUpdtFormCycle) return;//raceCond
			this._lastWspData = FORMS.formToJson(this._form);
			await this.refreshBtnsState();
			if (currentUpdtFormCycle != this._pendingUpdtFormCycle) return;//raceCond
			this._msg.setStandardMsg(null);
			this._form.checkValidity();
			DOM.setHidden(this._form, false);
		} catch (e) {
			this._msg.setCustomMsg("Chargement des propriétés de l'atelier en erreur", "error");
			return ERROR.log(e);
		}
	}

	onViewHidden(closed?: boolean): void {
		if (closed && this.wsp)	(this.reg.env as IWspUiEnv).place.closePlace();
	}

	onViewBeforeHide(close?: boolean): boolean {
		return false;
	}

	async onViewWaitForHide(close?: boolean): Promise<boolean> {
		if (!this.config.checkDirtyOnClose || !await this.isDirty() || await POPUP.confirm("Attention, vos modifications n'ont pas été enregistrées.", this, {cancelLbl: "Annuler", okLbl: "Quitter sans enregistrer"})) {
			await this._doRevert();
			return true;
		}
		return false;
	}

	_listenLiveStateChange: boolean = true;

	async onWspLiveStateChange(wsp: Wsp) {
		if (wsp !== this.wsp || !wsp) return;
		if (wsp.isDeleted === true) {
			POPUP.showNotif("L'atelier a été supprimé", POPUP.findPopupableParent(this), {autoHide: 3000});
			return this._doClose();
		} else {
			if (this._listenLiveStateChange)
				return this.updateForm(false);
		}
	}
}

REG.reg.registerSkin('c-wsp-props', 1, /* language=CSS */ `
	:host {
		min-height: 12em;
	}

	#form {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		flex: 1;
		padding: 1em;
		overflow: auto;
	}

	#form[hidden] {
		display: none;
	}

	.fieldslist {
		border: none;
		padding: .5rem;
		margin: .5rem;
		min-width: 0;
	}

	.fieldslist > .fields {
		display: grid;
		min-width: 0;
		grid-template-columns: auto 1fr;
		grid-row-gap: .7rem;
		grid-auto-rows: max-content;
	}

	.fieldslist > .fields:first-child {
		margin-inline-end: 2em;
	}

	.fields > div {
		display: contents;
	}

	.fields > *.allCols {
		grid-column-start: 1;
		grid-column-end: 3;
	}

	.fields div[hidden] {
		display: none !important;
	}

	c-action {
		margin: 0 .5em 0 0;
	}

	textarea[name=desc] {
		height: 3em;
		resize: none;
	}

	.details {
		font-size: .8em;
	}

	.deprecated {
		font-style: italic;
		color: var(--fade-color);
	}

	.titleDeprecated {
		font-variant-position: sub;
		font-size: .8em;
		margin-inline-start: .5rem;
		color: var(--warning-color);
	}

	.important {
		font-weight: bolder;
		color: var(--alt1-color)
	}
`);

//customElements.define('c-wsp-props', WspProps);

/**
 * Création d'un atelier
 * @return sur le close : Wsp créé ou null
 */
export class WspCreateProps extends WspProps<IChainEnv> {
	protected async _initialize(init: OWspPropsInit<IChainEnv>) {
		init.disableRevert = true;
		await super._initialize(init);
		this._saveBtn.label = "Créer";
	}

	get areaListBase(): string[] {
		//
		let list: string[] = ["wspProps:create:commons"];
		switch (this.reg.env.universe.config.backEnd) {
		case "fs":
			list.push("wspProps:create:fs");
			break;
		case "odb":
			list.push("wspProps:create:db", "wspProps:create:db:other");
			break;
		}
		return list;
	}

	get areaList(): string[] {
		let list = this.areaListBase;
		list.push("wspProps:create:fromScratch");
		return list;
	}

	protected async getIniParams(): Promise<Jsonisable> {
		let datas = await super.getIniParams();
		/// Propriétés jamais récupérées dans le cas de créationn d'un atelier à partir d'un autre
		delete datas.title;
		delete datas.alias;
		delete datas.code;
		return datas;
	}

	private _existingWspmetaOnPathFs: JWspTypeInst | null;

	protected async refreshBtnsState(): Promise<void> {
		if (this.reg.env.universe.config.backEnd === "fs") {
			let wspTypeDiv = this._form.querySelector("*[area-id='wspType']") as HTMLDivElement;
			let wspTypeSelector = this._form.querySelector("wsptype-selector") as WspTypeSelector;
			// On vérifie l'existence de contenus à l'emplacement
			const params = await this.getParamsToSave();
			this._existingWspmetaOnPathFs = params.code ? await WSP.fetchExistWspFsOnPath(this.reg.env.universe.wspServer, params.code, params.folderContent) : null;
			this._saveBtn.label = this._existingWspmetaOnPathFs ? "Créer (récupérer l'atelier existant)" : "Créer";
			if (wspTypeDiv) DOM.setHidden(wspTypeDiv, this._existingWspmetaOnPathFs != null);
			if (wspTypeSelector) wspTypeSelector.disabled = this._existingWspmetaOnPathFs != null;
		}
		return super.refreshBtnsState();
	}

	async _doExecuteValid(): Promise<void> {
		try {
			const params = await this.getParamsToSave();
			let wspTypeInst;
			if (this.reg.env.universe.config.backEnd === "fs" && this._existingWspmetaOnPathFs) {
				// mode FS ET atelier existant => on récupère le wspMeta
				wspTypeInst = this._existingWspmetaOnPathFs;
			} else {
				let wspTypeSelector: WspTypeSelector = this._form.querySelector("wsptype-selector") as WspTypeSelector;
				wspTypeInst = wspTypeSelector.fillWspTypeInst();
			}
			const resp = await WSP.createWsp(this.reg.env.universe.wspServer, params, wspTypeInst);
			if (!resp)
				throw "no resp";
			return this._doClose(resp);
		} catch (e) {
			await ERROR.report("Création de l'atelier impossible.", e);
			await this.refreshBtnsState();
		}
	}

}

customElements.define('c-wsp-create-props', WspCreateProps);

/**
 * Création d'un atelier à partir d'une archive
 * @return sur le close : Wsp créé ou null
 */
export class WspCreateFromScwspProps extends WspCreateProps {

	get areaList(): string[] {
		let list = this.areaListBase;
		list.push("wspProps:create:fromScwsp");
		return list;
	}

	async _doExecuteValid(): Promise<void> {
		try {
			const params = await this.getParamsToSave();
			//scwspFile
			let wspTypeSelector: WspTypeSelector = this._form.querySelector("wsptype-selector") as WspTypeSelector;
			let scWspInput = this._form.elements.namedItem("scwspFile") as HTMLInputElement;
			if (!scWspInput.files || scWspInput.files.length == 0) throw "No scwsp file selected";
			const resp = await WSP.importWsp(this.reg.env.universe.wspServer, params, scWspInput.files.item(0));
			if (!resp)
				throw "no resp";
			this._doClose(resp);
		} catch (e) {
			await ERROR.report("Import de l'atelier impossible.", e);
			await this.refreshBtnsState();
		}
	}

}

customElements.define('c-wsp-create-fromscwsp-props', WspCreateFromScwspProps);

/**
 * Création d'un atelier DRF (dépendant d'un autre atelier)
 */
export class WspCreatDrfProps extends WspProps<IWspUiEnv> {

	protected async _initialize(init: OWspPropsInit<IWspUiEnv>) {
		init.disableRevert = true;
		await super._initialize(init);
		this._saveBtn.label = "Créer";
	}

	get areaList(): string[] {
		return ["wspProps:create:commons", "wspProps:create:fromScratch", "wspProps:create:db", "wspProps:create:db:drf"];
	}

	protected async getIniParams(): Promise<Jsonisable> {
		return this._emptyData;
	}

	async _doExecuteValid(): Promise<void> {
		try {
			const params = await this.getParamsToSave();
			let wspTypeSelector: WspTypeSelector = this._form.querySelector("wsptype-selector") as WspTypeSelector;
			const resp = await WSP_DRF.createWspDrf(this.reg.env.wsp, params, wspTypeSelector.fillWspTypeInst());
			if (!resp)
				throw "no resp";
			this._doClose(resp);
		} catch (e) {
			await ERROR.report("Création de l'atelier impossible.", e);
			await this.refreshBtnsState();
		}
	}
}

customElements.define('c-wsp-create-drf-props', WspCreatDrfProps);

/**
 * Création d'un atelier DRV (dépendant d'un autre atelier)
 */
export class WspCreatDrvProps extends WspProps<IWspUiEnv> {

	protected async _initialize(init: OWspPropsInit<IWspUiEnv>) {
		init.disableRevert = true;
		await super._initialize(init);
		this._saveBtn.label = "Créer";
	}

	get areaList(): string[] {
		return ["wspProps:create:commons", "wspProps:create:fromScratch", "wspProps:create:db", "wspProps:create:db:drv"];
	}

	protected async getIniParams(): Promise<Jsonisable> {
		return this._emptyData;
	}

	async _doExecuteValid(): Promise<void> {
		try {
			const params = await this.getParamsToSave();
			let wspTypeSelector: WspTypeSelector = this._form.querySelector("wsptype-selector") as WspTypeSelector;
			return this._doClose(await WSP_DRV.createWspDrv(this.reg.env.wsp, params, wspTypeSelector.fillWspTypeInst()));
		} catch (e) {
			await ERROR.report("Création de l'atelier impossible.", e);
			await this.refreshBtnsState();
		}
	}
}

customElements.define('c-wsp-create-drv-props', WspCreatDrvProps);

/**
 * Modification / affichage des propriétés d'un atelier
 */

export interface OWspEditInit extends OSkinableInit {
	reg: IReg<IWspUiEnv>
	tabAreasLists?: string
}

export class WspEditProps extends BaseAreaViewAsync<IReg<IWspUiEnv>> implements IViewsContainer {
	reg: IReg<IWspUiEnv>
	_tabs: Tabs;
	_msg: MsgLabel;

	protected async _initialize(init: OWspEditInit) {
		this.reg = init.reg;
		this._msg = <MsgLabel/> as MsgLabel;
		this.reg.addToList("wsp-edit-tab", "infoArea", 1, new AreaAsync<OWspPropsInit<IWspUiEnv>, WspProps<IWspUiEnv>>()
			.setLabel("Propriétés")
			.setBodyBuilder((ctx) => {
				return <WspEditProps_info î={ctx} code="props"/> as WspEditProps_info;
			}), 1);
		this.reg.addToList("wsp-edit-tab", "advancedArea", 1, new AreaAsync<OWspPropsInit<IWspUiEnv>, WspProps<IWspUiEnv>>()
			.setLabel("Avancé")
			.setBodyBuilder((ctx) => {
				return <WspEditProps_advanced î={ctx} code="advanced"/> as WspEditProps_advanced;
			}), 101);

		// TODO : onglets "Permissions"

		// Contexte des actions : this
		this._tabs = <Tabs areas-context="wsp-edit-tab-ctxt" areas-list={init.tabAreasLists || "wsp-edit-tab"}/> as Tabs;
		this.reg.registerSvc("wsp-edit-tab-ctxt", 1, {
			reg: this.reg
		} as OWspPropsInit<IWspUiEnv>);

		this._attach(this.tagName, init, this._tabs);
		this.reg.installSkin("webzone:panel", this.shadowRoot);
	}

	onViewShown(): void {
		this._msg.setStandardMsg("loading");
		this.reg.env.wsp.waitForLoad().then(async () => {
			if (this.reg.env.wsp.backEnd === "odb" && await WSP.checkWspSvc(this.reg.env.wsp, this, "skinSet")) {
				this._tabs.addTab(new AreaAsync<OWspPropsInit<IWspUiEnv>, WspProps<IWspUiEnv>>()
					.setLabel("Habillages graphiques")
					.setDescription("Habillages graphiques des générateurs du modèle documentaire")
					.setBodyBuilder((ctx) => {
						return <WspEditProps_skins î={ctx} code="props"/> as WspEditProps_skins;
					}), {position: 1});
			}
			this._msg.setStandardMsg(null);
		})
	}

	onViewBeforeHide(close?: boolean): boolean {
		return false;
	}

	async onViewWaitForHide(close?: boolean): Promise<boolean> {
		return this._tabs.selectedTab.view.onViewWaitForHide(close);
	}

	visitViews(visitor: (view: IView) => any, options?: OViewVisitOptions): any {
		if (this._tabs) this._tabs.visitViews(visitor, options);
	}

	visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		if (this._tabs)
			return this._tabs.visitViewsAsync(visitor, options);
	}

}

customElements.define('c-wsp-edit-props', WspEditProps);
REG.reg.registerSkin('c-wsp-edit-props', 1, /* language=CSS */ `
	:host {
		display: flex;
		flex: 1;
		min-width: 0;
		min-height: 0;
	}

	c-tabs {
		flex: 1;
	}
`)

class WspEditProps_info extends WspProps<IWspUiEnv> {
	protected async _initialize(init: OWspPropsInit<IWspUiEnv>) {
		init.checkDirtyOnClose = true;
		init.exportScwsp = true;
		return super._initialize(init);
	}

	protected initFormELt(form: HTMLFormElement): void {
		form.appendChild(
			<fieldset class="fieldslist">
				<div class="fields" area-ids="*"/>
			</fieldset>);
		form.appendChild(
			<fieldset class="fieldslist" id="wspTypeFs">
				<div class="fields" area-ids="wspType"/>
			</fieldset>);
	}

	get areaList(): string[] {
		const wspDefProps = this.wsp.wspDefProps;
		let list: string[] = ["wspProps:edit:commons:infos"];
		switch (this.wsp.backEnd) {
		case "fs":
			list.push("wspProps:edit:fs:infos");
			break;
		case "odb":
			list.push("wspProps:edit:db:infos");
			break;
		}
		if (wspDefProps && wspDefProps.drfRefWsp)
			list.push("wspProps:edit:db:drf:infos");
		else if (wspDefProps && wspDefProps.drvAxis)
			list.push("wspProps:edit:db:drv:infos");
		else if (this.wsp.backEnd == "odb")
			list.push("wspProps:edit:db:other:infos");
		return list;
	}

	async _doExecuteValid(): Promise<void> {
		try {
			const allFormsDatas = await this.getParamsToSave();
			// Filtre newParams au juste nécessaire (params modifiés)
			let newParams: Jsonisable = {};
			let hasNewParams = false;
			for (const props in allFormsDatas) {
				if (props != "wspType")
					if (this._lastWspData[props] !== allFormsDatas[props]) {
						hasNewParams = true;
						newParams[props] = allFormsDatas[props];
					}
			}
			if (hasNewParams) await this.wsp.updateWspProps(newParams);
			if (JSON.stringify(allFormsDatas.wspType) != JSON.stringify(this._lastWspData.wspType)) {
				let isMigrationNeeded = await this.wsp.isMigrationNeeded(allFormsDatas.wspType);
				if (isMigrationNeeded === false) {
					await this.wsp.updateWspType(allFormsDatas.wspType, false);
					//return this.onWspLiveStateChange(this.wsp); NON : l'interface est actualisée ave cles remontées WS
				} else if (isMigrationNeeded === true) {
					const result = await POPUP.confirmCheck(<div>
							<div>Les contenus de votre atelier doivent être mis à jour.</div>
							<div><b>Vous ne pourrez pas annuler cette action.</b></div>
							<div>Voulez-vous poursuivre l'enregistrement du modèle documentaire ?</div>
						</div>,
						this, "Exécuter la mise à jour des contenus", true, {okLbl: "Poursuivre", cancelLbl: "Annuler"});
					if (result === false) await this._doRevert();
					else await this.wsp.updateWspType(allFormsDatas.wspType, result === "checked");
					//return this.onWspLiveStateChange(this.wsp); NON : l'interface est actualisée avec les remontées WS
				} else {
					let msg = isMigrationNeeded.getAttribute("desc");
					POPUP.showNotifError(`Une mise à jour automatique des contenus de l'atelier serait nécessaire, mais des modifications manuelles préalables doivent être réalisées : ${msg}`, this, {noCloseBtn: true, kind: "warning",});
					await this._doRevert();
					//return this.onWspLiveStateChange(this.wsp); NON : l'interface est actualisée avec les remontées WS
				}
			}
		} catch (e) {
			await ERROR.report("Modification des propriétés de l'atelier impossible.", e);
			await this.refreshBtnsState();
		}
	}

	protected isReadOnlyFieldset(fieldsetElt: HTMLFieldSetElement): boolean {
		if (fieldsetElt.id === "wspTypeFs")
			return !this.reg.hasPerm("action.wspProps#update.wsptype");
		else
			return !this.reg.hasPerm("action.wspProps#update.props");
	}

}

customElements.define('c-wsp-edit-props-info', WspEditProps_info);

class WspEditProps_advanced extends WspEditProps_info {

	get areaList(): string[] {
		const wspDefProps = this.wsp.wspDefProps;
		let list: string[] = ["wspProps:edit:commons:advanced"];
		switch (this.wsp.backEnd) {
		case "fs":
			list.push("wspProps:edit:fs:advanced");
			break;
		case "odb":
			list.push("wspProps:edit:db:advanced");
			break;
		}
		if (wspDefProps && wspDefProps.drfRefWsp)
			list.push("wspProps:edit:db:drf:advanced");
		else if (wspDefProps && wspDefProps.drvAxis)
			list.push("wspProps:edit:db:drv:advanced");
		else if (this.wsp.backEnd == "odb")
			list.push("wspProps:edit:db:other:advanced");
		return list;
	}

}

customElements.define('c-wsp-edit-props-advanced', WspEditProps_advanced);

class WspEditProps_skins extends WspEditProps_info {

	get areaList(): string[] {
		return ["wspProps:edit:db:other:skins"]
	}

}

customElements.define('c-wsp-edit-props-skins', WspEditProps_skins);

/**
 * Suppression d'un atelier
 */
export class WspRemoveProps<E extends IWspUiEnv> extends WspProps<E> {
	protected _ctrlElt: HTMLDivElement;

	protected async _initialize(init: OWspPropsInit<E>) {
		const wspTitle = init.reg.env.wsp.wspTitle;
		init.disableRevert = true;
		await super._initialize(init);
		this.shadowRoot.insertBefore(<div class="question"><span id="kind"/>{`Voulez-vous vraiment supprimer l'atelier '${wspTitle}' ?`}</div>, this.shadowRoot.firstElementChild);
		this._saveBtn.label = "Supprimer";
	}

	protected initFormELt(form: HTMLFormElement): void {
		super.initFormELt(form);
		// Controles DRV / RRF
		this._ctrlElt = form.appendChild(<div class="warning">
			<span id="kind"/>
			<div class="msgs"/>
		</div>) as HTMLDivElement;
	}

	protected async updateForm(clear: boolean = true): Promise<void> {
		await super.updateForm(clear);
		let msgs = [];
		// Refresh utile de l'infoWsp car ces propriétés de sont pas dynamiquement rafraichies
		let infoWsp = await this.wsp.wspServer.config.adminWspUrl.fetchJson<JInfoWsp>(IO.qs("cdaction", "InfoWsp", "param", this.wsp.code, "wspPropsOpts", "(drvWspUsedBy 'true' listDrvs 'true' listDrfs 'true')"));

		if (infoWsp.props.drvWspUsedBy) {
			let wspListArray = infoWsp.props.drvWspUsedBy.split(" ");
			let wspList = wspListArray.join(", ");
			if (wspListArray.length == 1)
				msgs.push(`Cet atelier est source du calque de dérivation '${wspList}'. La suppression de cet atelier est donc impossible.`);
			else
				msgs.push(`Cet atelier est source des calques de dérivation : ${wspList}. La suppression de cet atelier est donc impossible.`);
			this._saveBtn.disabled = true;
		} else if (infoWsp.props.listDrvs || infoWsp.props.listDrfs) {
			if (infoWsp.props.listDrvs && this.wsp.wspServer.chain.config.drf)
				msgs.push(`Tous les ateliers 'calque de dérivation' et les ateliers 'calque de travail' liés seront également supprimés`);
			else if (infoWsp.props.listDrvs)
				msgs.push(`Tous les ateliers 'calque de dérivation' seront également supprimés`);

			if (infoWsp.props.listDrfs)
				msgs.push(`Tous les ateliers 'calque de travail', et les ateliers liés seront également supprimés`);

			this._saveBtn.label = "Supprimer les ateliers";
		}

		if (msgs.length == 0) {
			DOM.setHidden(this._ctrlElt, true);
		} else {
			let msgsElt = this._ctrlElt.querySelector(".msgs") as HTMLDivElement;
			msgs.forEach((msg) => {
				msgsElt.appendChild(<div>{msg}</div>)
			});
		}
	}

	protected async refreshBtnsState(): Promise<void> {
		this._form.checkValidity();
		this._saveBtn.disabled = false;
	}

	get areaList(): string[] {
		const wspDefProps = this.wsp.wspDefProps;
		let list: string[] = ["wspProps:remove:commons"];
		switch (this.wsp.backEnd) {
		case "fs":
			list.push("wspProps:remove:fs");
			break;
		case "odb":
			list.push("wspProps:remove:db");
			break;
		}
		if (wspDefProps && wspDefProps.drfRefWsp)
			list.push("wspProps:remove:db:drf");
		else if (wspDefProps && wspDefProps.drvAxis)
			list.push("wspProps:remove:db:drv");
		else
			list.push("wspProps:remove:db:other");
		return list;
	}

	async _doExecuteValid(): Promise<void> {
		try {
			// Fermeture de l'atelier éventuellement ouvert dans le desk
			const mainApp = (desk as AppFrameDeskFeat).getMainApp();
			if ((mainApp as IApp<IWspEnv>)?.reg?.env.wsp?.equals(this.wsp))
				if (AppFrameDeskFeat.isIn(desk)) await desk.gotoHome();

			const params = await this.getParamsToSave();
			await this.wsp.delete(params);
			return this._doClose(true);
		} catch (e) {
			await ERROR.report("Suppression de l'atelier impossible.", e);
			return this.refreshBtnsState();
		}
	}
}

customElements.define('c-wsp-remove-props', WspRemoveProps);

REG.reg.registerSkin('c-wsp-remove-props', 1, /* language=CSS */ `
	*[hidden] {
		display: none !important;
	}

	.question, .warning {
		display: flex;
		align-items: center;
		margin: 1em;
	}

	.cplt {
		font-style: italic;
		padding-top: .3em;
	}

	.warning {
		color: var(--warning-color);
	}

	#kind {
		display: inline-block;
		min-width: 1.5em;
		min-height: 1.5em;
		margin: .5em;
	}

	.question > #kind {
		background: center no-repeat url(/@skin@/commons/widgets/popupable/question.svg);
	}

	.warning > #kind {
		background: center no-repeat url(/@skin@/commons/widgets/popupable/warning.svg);
	}
`);

/**
 * Suppression de plusieurs wsps
 */
export class WspsRemoveProps extends WspProps<IChainEnv> {
	protected wspsReg: IReg<IWspUiEnv>[];

	protected MSG_CONFIRM = "Voulez-vous vraiment supprimer l'atelier ?";
	protected MSG_CONFIRM_MULTI = "Voulez-vous vraiment supprimer les ateliers ?";
	protected MSG_ERROR = "Suppression de(s) atelier(s) en échec.";
	protected MSG_STATUS_REMOVED = "Supprimé";
	protected MSG_BTN = "Supprimer";

	protected async _initialize(init: OWspPropsInit<IChainEnv> & IRegsPointer<IWspUiEnv>) {
		init.disableRevert = true;
		this.wspsReg = init.regs;

		await super._initialize(init);
		this.reg.installSkin("c-wsp-remove-props", this.shadowRoot);
		this._onSaveShowWaitMsg = false;
		let cplt = "";
		if (this.reg.env.universe.wspServer.chain.config.drf && this.reg.env.universe.wspServer.chain.config.drv)
			cplt = "Tous les éventuels ateliers 'calque de dérivation', et 'calque de travail' liés seront également supprimés";
		else if (this.reg.env.universe.wspServer.chain.config.drf)
			cplt = "Tous les éventuels ateliers 'calque de travail' liés seront également supprimés";
		else if (this.reg.env.universe.wspServer.chain.config.drv)
			cplt = "Tous les éventuels ateliers 'calque de dérivation' liés seront également supprimés";

		let wsps: Wsp[] = [];
		this.wspsReg.forEach((entry) => {wsps.push(entry.env.wsp)});
		WSP.sortWspsByDeep(wsps).reverse();
		let count = wsps.length;

		let msg = count > 1 ? this.MSG_CONFIRM_MULTI : this.MSG_CONFIRM;
		this.shadowRoot.insertBefore(<div>
			<div class="question">
				<span id="kind"/>
				<div>
					<div>{msg}</div>
					<div class="cplt">{cplt}</div>
				</div>
			</div>
			<WspsMonitoring î={{
				wsps: wsps,
				defaultStateKey: "toRemove",
				states: [{key: "toRemove", level: "disabled", label: "À supprimer"}]
			} as OWspsMonitoringInit}/>
		</div>, this.shadowRoot.firstElementChild);

		this._saveBtn.label = this.MSG_BTN;
	}

	async _doExecuteValid(): Promise<void> {
		let wspTitleErrosArray: string[] = [];
		let dialog: IPopupable;
		try {
			const params = await this.getParamsToSave();
			let wsps: Wsp[] = [];
			this.wspsReg.forEach((entry) => {wsps.push(entry.env.wsp)});
			WSP.sortWspsByDeep(wsps).reverse();
			let wspsMonitoring = <WspsMonitoring î={{
				wsps: wsps,
				defaultStateKey: "notStart",
				states: [
					{key: "notStart", level: "disabled", label: "En attente"},
					{key: "toRemove", level: "disabled", label: "À supprimer"},
					{key: "pending", level: "pending", label: "En cours..."},
					{key: "removed", level: "success", label: this.MSG_STATUS_REMOVED},
					{key: "failed", level: "error", label: "Échec"}
				]
			} as OWspsMonitoringInit}/> as WspsMonitoring;

			dialog = POPUP.showDialog(<div>
				<MsgLabel label="Suppression en cours..."/>
				{wspsMonitoring}
			</div>, this, {
				skinOver: "c-wsps-props-monitoraction",
				fixSize: false,
				titleBar: null,
				initWidth: '50%',
			});
			let errorsArray: any[] = [];
			for (const entry in wsps) {
				let wsp = wsps[entry];
				wspsMonitoring.updateWspStatus(wsp.code, "pending");
				try {
					await this.doDelete(wsp, params);
					wspsMonitoring.updateWspStatus(wsp.code, "removed");
					await new Promise(resolve => setTimeout(resolve, 1000));
				} catch (e) {
					wspsMonitoring.updateWspStatus(wsp.code, "failed");
					wspTitleErrosArray.push(wsp.wspTitle);
					errorsArray.push(e);
				}
			}
			await new Promise(resolve => setTimeout(resolve, 3000));
			if (errorsArray.length > 0) throw errorsArray;
			this._doClose();
		} catch (e) {
			let list = wspTitleErrosArray.join(", ");
			if (dialog) dialog.close();
			await ERROR.report(list ? this.MSG_ERROR : this.MSG_ERROR + " " + `Ateliers : '${list}'.`, e);
			await this._doClose();
		}
	}

	protected async doDelete(wsp: Wsp, params: any): Promise<void> {
		// Fermeture de l'atelier éventuellement ouvert dans le desk
		const mainApp = (desk as AppFrameDeskFeat).getMainApp();
		if ((mainApp as IApp<IWspEnv>)?.reg?.env.wsp?.equals(wsp))
			if (AppFrameDeskFeat.isIn(desk)) await desk.gotoHome();

		return wsp.delete(params);
	}

	protected async refreshBtnsState(): Promise<void> {
		this._form.checkValidity();
		this._saveBtn.disabled = false;
	}

	get areaList(): string[] {
		let list: string[] = ["wspProps:removeAll:commons"];
		switch (this.reg.env.universe.config.backEnd) {
		case "fs":
			list.push("wspProps:removeAll:fs");
			break;
		case "odb":
			list.push("wspProps:removeAll:db");
			break;
		}
		return list;
	}
}

REG.reg.registerSkin('c-wsps-remove-props', 1, /* language=CSS */ `
	wsps-monitoring {
		margin: 0 2em;
		max-height: 10em;
		overflow: auto;
	}
`);
REG.reg.registerSkin('c-wsps-props-monitoraction', 1, /* language=CSS */ `
	wsps-monitoring {
		margin: 0 2em;
		max-height: 15em;
		overflow: auto;
	}
`);
customElements.define('c-wsps-remove-props', WspsRemoveProps);

/**
 * Suppression définitive d'atelier(s)
 */
export class WspsRemovePermanentlyProps extends WspsRemoveProps {

	protected MSG_CONFIRM = "Voulez-vous vraiment supprimer définitivement l'atelier ?";
	protected MSG_CONFIRM_MULTI = "Voulez-vous vraiment supprimer définitivement les ateliers ?";
	protected MSG_ERROR = "Suppression définitive de(s) atelier(s) en échec.";
	protected MSG_STATUS_REMOVED = "Supprimé définitivement";
	protected MSG_BTN = "Supprimer définitivement";

	protected async doDelete(wsp: Wsp, params: any): Promise<void> {
		return wsp.deletePermanently(params);
	}

	get areaList(): string[] {
		let list: string[] = ["wspProps:remove:permanently:commons"];
		switch (this.reg.env.universe.config.backEnd) {
		case "fs":
			list.push("wspProps:remove:permanently:fs");
			break;
		case "odb":
			list.push("wspProps:remove:permanently:db");
			break;
		}
		return list;
	}

}

customElements.define('c-wsps-remove-permanently-props', WspsRemovePermanentlyProps);

/**
 * Edition des propriétés de plusieurs wsps
 */
export class WspsEditModelProps extends WspProps<IChainEnv> {
	protected wspsReg: IReg<IWspUiEnv>[];

	protected async _initialize(init: OWspPropsInit<IChainEnv> & IRegsPointer<IWspUiEnv>) {
		init.checkDirtyOnClose = true;
		init.disableRevert = true;
		this.wspsReg = init.regs;
		const count = this.wspsReg.length;
		await super._initialize(init);
		this.reg.installSkin("c-wsps-editmodel-props", this.shadowRoot);
		this._onSaveShowWaitMsg = false;

		let wsps: Wsp[] = [];
		this.wspsReg.forEach((entry) => {wsps.push(entry.env.wsp)});
		wsps = WSP.sortWspsByDeep(wsps);

		this.shadowRoot.insertBefore(<div>
			<div class="info">{`Édition des propriétés des '${count}' ateliers :`}</div>
			<WspsMonitoring î={{
				wsps: wsps,
				defaultStateKey: "toEdit",
				states: [{key: "toEdit", level: "disabled", label: "À éditer"}]
			} as OWspsMonitoringInit}/>
		</div>, this.shadowRoot.firstElementChild);
	}

	protected async refreshBtnsState(): Promise<void> {
		this._form.checkValidity();
		this._saveBtn.disabled = false;
	}

	async _doExecuteValid(): Promise<void> {
		let wspTitleErrosArray: string[] = [];
		let dialog: IPopupable;
		try {
			const allFormsDatas = await this.getParamsToSave();
			// Filtre newParams au juste nécessaire (params modifiés)
			let newParams: Jsonisable = {};
			let hasNewParams = false;
			for (const props in allFormsDatas) {
				if (props != "wspType" && props != "doMigrateIfNecessary")
					hasNewParams = true;
				newParams[props] = allFormsDatas[props];
			}

			let wsps: Wsp[] = [];
			this.wspsReg.forEach((entry) => {wsps.push(entry.env.wsp)});
			wsps = WSP.sortWspsByDeep(wsps);

			let wspsMonitoring = <WspsMonitoring î={{
				wsps: wsps,
				defaultStateKey: "notStart",
				states: [
					{key: "notStart", level: "disabled", label: "En attente"},
					{key: "toUpdate", level: "disabled", label: "À actualiser"},
					{key: "pending", level: "pending", label: "En cours..."},
					{key: "updated", level: "success", label: "Actualisé"},
					{key: "failed", level: "error", label: "Échec"}
				]
			} as OWspsMonitoringInit}/> as WspsMonitoring;

			dialog = POPUP.showDialog(<div>
				<MsgLabel label="Enregistrements en cours..."/>
				{wspsMonitoring}
			</div>, this, {
				skinOver: "c-wsps-props-monitoraction",
				fixSize: false,
				titleBar: null,
				initWidth: '50%',
			});
			let errorsArray: any[] = [];
			for (const entry in wsps) {
				let wsp = wsps[entry];
				wspsMonitoring.updateWspStatus(wsp.code, "pending");
				try {
					if (hasNewParams) await wsp.updateWspProps(newParams);
					await wsp.updateWspType(allFormsDatas.wspType, allFormsDatas["doMigrateIfNecessary"]);
					wspsMonitoring.updateWspStatus(wsp.code, "updated");
					await new Promise(resolve => setTimeout(resolve, 1000));
				} catch (e) {
					wspsMonitoring.updateWspStatus(wsp.code, "failed");
					wspTitleErrosArray.push(wsp.wspTitle);
					errorsArray.push(e);
				}
			}
			await new Promise(resolve => setTimeout(resolve, 3000));
			if (errorsArray.length > 0) throw errorsArray;
			this._doClose();
		} catch (e) {
			let list = wspTitleErrosArray.join(", ");
			if (dialog) dialog.close();
			await ERROR.report(list ? "Édition des ateliers en échec." : `Édition des ateliers ${list} en échec.`, e);
			await this.refreshBtnsState();
		}
	}

	/*protected async getIniParams(): Promise<Jsonisable> {
		// Par approximation, on considère que le premier atelier de la liste fournit les valeurs d'initialisation du formulaire
		let wsp = this.wspsReg && this.wspsReg[0] ? this.wspsReg[0].reg.env.wsp : null;
		if (wsp) {
			await wsp.waitForLoad();
			return wsp.makeParamsForUpdate();
		} else
			return this._emptyData;
	}*/

	protected areasContext(): IControlAreaContext & IRegsPointer<IWspUiEnv> & IRegPointer<IChainEnv> {
		return {
			reg: this.reg,
			regs: this.wspsReg,
			buildControlLabel: true
		};
	}

	protected initFormELt(form: HTMLFormElement): void {
		form.appendChild(
			<fieldset class="fieldslist" id="wspTypeFs">
				<div class="fields" area-ids="*"/>
			</fieldset>);
	}

	get areaList(): string[] {
		let list: string[] = ["wspProps:editAll:commons"];
		switch (this.reg.env.universe.config.backEnd) {
		case "fs":
			list.push("wspProps:editAll:fs");
			break;
		case "odb":
			list.push("wspProps:editAll:db");
			break;
		}
		if (this.reg.env.universe.config.drv)
			list.push("wspProps:editAll:db:drv");
		if (this.reg.env.universe.config.drf)
			list.push("wspProps:editAll:db:drf");
		return list;
	}

}

REG.reg.registerSkin('c-wsps-editmodel-props', 1, /* language=CSS */ `
	.info {
		display: flex;
		align-items: center;
		margin: 1em;
	}

	wsps-monitoring {
		margin: 0 2em;
		max-height: 6em;
		overflow: auto;
	}
`);
customElements.define('c-wsps-editmodel-props', WspsEditModelProps);